import { css, html, LitElement, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import * as Styles from "../../styles.ts";
import { type Slug } from "../../../models/slug.ts";
import { type ForceNote } from "../../../models/note/force.ts";
import * as Arc from "../../../models/arc.ts";
import * as Certainty from "../../../models/certainty.ts";
import * as Impact from "../../../models/impact.ts";
import * as PaceLayer from "../../../models/pace-layer.ts";
import "./category-icon.ts";
import { customEvent } from "../../util/event.ts";

export const activateForceEvent = customEvent<{ slug: string }>(
  "activate-force-row",
  { bubbles: true, composed: true },
);

const styles = css`
  :host {
    --row-height: var(--space-12);
    --row-pad-x: var(--space-2);
    --row-pad-y: var(--space-1);
    display: block;
    width: 100cqw;
    container-type: size;
  }

  .tablewindow {
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
    line-height: var(--line-height-snug);
    width: 100cqw;
    scrollbar-color: var(--muted-foreground) transparent;
    scrollbar-width: thin;
    overflow-x: auto;
  }

  :host([fixed]) {
    height: 100cqh;
  }

  :host([fixed]) .tablewindow {
    overflow-y: auto;
    height: 100cqh;
  }

  .table {
    border-collapse: collapse;
    min-width: 100%;
  }

  .table thead {
    background-color: var(--muted);
    position: sticky;
    top: 0;
  }

  .table th {
    white-space: nowrap;
    height: var(--row-height);
    font-weight: 600;
  }

  .table tbody tr {
    cursor: pointer;
    white-space: nowrap;
    height: var(--row-height);
  }

  .table td,
  .table th {
    text-align: left;
    padding: var(--row-pad-y) var(--row-pad-x);
    align-items: center;
    max-width: 20em;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .table tr {
    --force-table-row-bg: var(--card);
    background-color: var(--force-table-row-bg);
  }

  .table tr:nth-child(even) {
    --force-table-row-bg: var(--muted);
  }

  .table tbody tr:hover {
    background-color: color-mix(
      in oklch,
      var(--force-table-row-bg),
      var(--foreground) 8%
    );
  }

  td.forcerow-category {
    padding-left: calc(var(--row-pad-x) * 2);
    width: 0px;
  }

  .forcerow-title {
    font-weight: 600;
    width: 400px;
  }

  .forcerow-desc {
    color: var(--muted-foreground);
  }
`;

export const ForceRow = (force: ForceNote): TemplateResult => {
  const onForceRowClick = (event: MouseEvent) => {
    if (force.slug == undefined) {
      return;
    }

    event.target?.dispatchEvent(activateForceEvent({ slug: force.slug }));
  };

  return html`
    <tr class="forcerow" @click="${onForceRowClick}">
      <td class="forcerow-category">
        <category-icon .category="${force.meta.category}"></category-icon>
      </td>
      <td class="forcerow-title">${force.title}</td>
      <td class="forcerow-uncertainty">
        ${Certainty.toDesc(force.meta.certainty)}
      </td>
      <td class="forcerow-impact">${Impact.toDesc(force.meta.impact)}</td>
      <td class="forcerow-arc">${Arc.toDesc(force.meta.arc)}</td>
      <td class="forcerow-layer">${PaceLayer.toDesc(force.meta.pace)}</td>
      <td class="forcerow-desc">${force.content}</td>
    </tr>
  `;
};

const getForceSlug = (force: ForceNote) => force.slug;

@customElement("force-table")
export class ForceTableElement extends LitElement {
  static override styles = [Styles.base, styles];

  @property()
  accessor forces: Map<Slug, ForceNote> = new Map();

  override render() {
    return html`
      <div class="tablewindow">
        <table class="table">
          <thead>
            <tr>
              <th></th>
              <th>Force</th>
              <th>P%</th>
              <th>Impact</th>
              <th>Arc</th>
              <th>Pace</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            ${repeat(this.forces.values(), getForceSlug, (force) =>
              ForceRow(force),
            )}
          </tbody>
        </table>
      </div>
    `;
  }
}
